import { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_BASE = '/api';

function formatCurrency(value) {
  if (!value) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function StatusBadge({ status }) {
  const styles = {
    initiated: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    in_progress: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    cancelled: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };

  const icons = {
    initiated: Clock,
    in_progress: RefreshCw,
    completed: CheckCircle,
    cancelled: XCircle,
  };

  const Icon = icons[status] || Clock;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.initiated}`}>
      <Icon className="w-3 h-3" />
      {status.replace('_', ' ')}
    </span>
  );
}

function ItemStatusBadge({ status }) {
  const styles = {
    pending: 'text-slate-400',
    transferred: 'text-emerald-400',
    fractional_liquidated: 'text-sky-400',
    discrepancy: 'text-amber-400',
    resolved: 'text-emerald-400',
    missing: 'text-red-400',
  };

  const icons = {
    pending: Clock,
    transferred: CheckCircle,
    fractional_liquidated: CheckCircle,
    discrepancy: AlertCircle,
    resolved: CheckCircle,
    missing: XCircle,
  };

  const Icon = icons[status] || Clock;
  
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${styles[status]}`}>
      <Icon className="w-3 h-3" />
      {status.replace('_', ' ')}
    </span>
  );
}

function TransferCard({ transfer, onReconcile, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [carryOverCostBasis, setCarryOverCostBasis] = useState(true);

  const loadDetails = async () => {
    if (details) {
      setExpanded(!expanded);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/transfers/${transfer.id}`);
      const data = await response.json();
      setDetails(data);
      setExpanded(true);
    } catch (err) {
      console.error('Error loading transfer details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const response = await fetch(`${API_BASE}/transfers/${transfer.id}/reconcile`, {
        method: 'POST',
      });
      const data = await response.json();
      setDetails(prev => ({ ...prev, ...data }));
      if (onReconcile) onReconcile(data);
    } catch (err) {
      console.error('Error reconciling:', err);
    } finally {
      setReconciling(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const params = new URLSearchParams({
        close_source_account: 'true',
        carry_over_cost_basis: carryOverCostBasis.toString(),
      });
      
      const response = await fetch(`${API_BASE}/transfers/${transfer.id}/complete?${params}`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (data.status === 'completed') {
        setShowCompleteModal(false);
        alert(`Transfer completed!\n\nCost basis carried for ${data.cost_basis_carried?.length || 0} positions.\nSource account positions closed: ${data.positions_zeroed?.length || 0}`);
        if (onRefresh) onRefresh();
      } else {
        alert(`Error: ${data.detail || JSON.stringify(data)}`);
      }
    } catch (err) {
      console.error('Error completing transfer:', err);
      alert(`Error completing transfer: ${err.message}`);
    } finally {
      setCompleting(false);
    }
  };

  const isActive = transfer.status === 'initiated' || transfer.status === 'in_progress';
  const canComplete = transfer.status === 'in_progress' && details?.all_reconciled;

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-slate-200">
            <span className="font-medium">{transfer.source_account}</span>
            <ArrowRight className="w-4 h-4 text-slate-500" />
            <span className="font-medium">{transfer.target_account}</span>
          </div>
          <StatusBadge status={transfer.status} />
        </div>
        
        <div className="flex items-center gap-2">
          {isActive && (
            <button
              onClick={handleReconcile}
              disabled={reconciling}
              className="btn btn-primary text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} />
              Reconcile
            </button>
          )}
          {canComplete && (
            <button
              onClick={() => setShowCompleteModal(true)}
              className="btn bg-emerald-600 hover:bg-emerald-500 text-white text-sm"
            >
              <CheckCircle className="w-4 h-4" />
              Complete
            </button>
          )}
          <button
            onClick={loadDetails}
            disabled={loading}
            className="btn btn-ghost text-sm"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-slate-500">Initiated</p>
          <p className="text-slate-300">{transfer.initiated_date}</p>
        </div>
        <div>
          <p className="text-slate-500">Expected</p>
          <p className="text-slate-300">{transfer.expected_completion_date || 'N/A'}</p>
        </div>
        <div>
          <p className="text-slate-500">Positions</p>
          <p className="text-slate-300">{transfer.items_count}</p>
        </div>
        <div>
          <p className="text-slate-500">Value</p>
          <p className="text-slate-300">{formatCurrency(transfer.expected_total_value)}</p>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && details && (
        <div className="mt-4 pt-4 border-t border-slate-700/50">
          {/* Status Counts */}
          {details.items_by_status && (
            <div className="mb-4 flex flex-wrap gap-2">
              {Object.entries(details.items_by_status).map(([status, items]) => (
                <span key={status} className="text-xs px-2 py-1 rounded bg-slate-700/50">
                  <ItemStatusBadge status={status} /> × {items.length}
                </span>
              ))}
            </div>
          )}

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 text-left">
                  <th className="pb-2">Ticker</th>
                  <th className="pb-2">Expected</th>
                  <th className="pb-2">Actual</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Notes</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {details.items_by_status && Object.entries(details.items_by_status).map(([status, items]) => 
                  items.map((item, idx) => (
                    <tr key={`${status}-${item.ticker}-${idx}`} className="border-t border-slate-700/30">
                      <td className="py-2 font-medium">{item.ticker}</td>
                      <td className="py-2 font-mono">{item.expected_shares?.toFixed(4)}</td>
                      <td className="py-2 font-mono">{item.actual_shares?.toFixed(4) || '-'}</td>
                      <td className="py-2"><ItemStatusBadge status={status} /></td>
                      <td className="py-2 text-xs text-slate-400 max-w-xs truncate">
                        {item.resolution_notes || 
                         (item.fractional_shares ? `${item.fractional_shares.toFixed(4)} fractional → $${item.fractional_value?.toFixed(2)}` : '-')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Fractional Summary */}
          {details.expected_fractional_liquidation > 0 && (
            <div className="mt-4 p-3 bg-slate-700/30 rounded-lg text-sm">
              <p className="text-slate-400">
                💡 Expected cash from fractional liquidation: <span className="text-emerald-400">{formatCurrency(details.expected_fractional_liquidation)}</span>
              </p>
            </div>
          )}

          {/* Complete Transfer Prompt - Show when reconciled but not completed */}
          {details.all_reconciled && transfer.status === 'in_progress' && (
            <div className="mt-4 p-4 bg-emerald-900/20 border border-emerald-700/50 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-400 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">All items reconciled!</span>
              </div>
              <p className="text-sm text-slate-300 mb-3">
                Ready to complete this transfer. Click the "Complete" button above to finalize.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Complete Transfer Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4">Complete Transfer</h3>
            
            <p className="text-slate-300 mb-4">
              This will mark the transfer as complete and close positions in <span className="text-amber-400">{transfer.source_account}</span>.
            </p>

            <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={carryOverCostBasis}
                  onChange={(e) => setCarryOverCostBasis(e.target.checked)}
                  className="mt-1 w-4 h-4 text-emerald-500 bg-slate-700 border-slate-600 rounded focus:ring-emerald-500"
                />
                <div>
                  <span className="text-white font-medium">Carry over cost basis</span>
                  <p className="text-sm text-slate-400 mt-1">
                    Copy cost basis from source account to target positions. 
                    <span className="text-emerald-400"> Recommended</span> if target brokerage hasn't received cost basis yet.
                  </p>
                </div>
              </label>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCompleteModal(false)}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={completing}
                className="btn bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {completing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Complete Transfer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TransferTracker({ onRefresh }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/transfers`);
      const data = await response.json();
      setTransfers(data.transfers || []);
    } catch (err) {
      console.error('Error loading transfers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransfers();
  }, []);

  const activeTransfers = transfers.filter(t => t.status === 'initiated' || t.status === 'in_progress');
  const completedTransfers = transfers.filter(t => t.status === 'completed' || t.status === 'cancelled');

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (transfers.length === 0) {
    return (
      <div className="card">
        <h3 className="card-header">
          <ArrowRight className="w-5 h-5 text-sky-400" />
          Account Transfers
        </h3>
        <p className="text-slate-400 text-sm">
          No transfers tracked yet. Use Claude to initiate a transfer: 
          "I'm transferring my Ameriprise account to Fidelity"
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Active Transfers */}
      {activeTransfers.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            Active Transfers
          </h3>
          <div className="space-y-4">
            {activeTransfers.map(t => (
              <TransferCard 
                key={t.id} 
                transfer={t} 
                onReconcile={loadTransfers}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </div>
      )}

      {/* Completed Transfers */}
      {completedTransfers.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            Completed Transfers
          </h3>
          <div className="space-y-4">
            {completedTransfers.map(t => (
              <TransferCard 
                key={t.id} 
                transfer={t}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

